import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CTAButton } from './CTAButton';
import { trackEvent } from '../utils/tracking';

// Hero "Story": "Tu cuerpo no falló" y la presentación de H.E.R. en una sola
// experiencia de ~6 segundos, hecha con CSS y texto real (sin video):
//   0 Tu cuerpo no falló → 1 Lo que antes funcionaba… → 2 No necesitás hacer más… → 3 H.E.R. y el Protocolo.
// Las escenas van EN SECUENCIA: cada una sale por completo, hay un instante oscuro y recién entonces entra
// la siguiente. Nunca hay dos textos visibles a la vez.
// Mientras corre el relato la escena ocupa el alto de la pantalla. Al terminar, la sección se ajusta al alto
// REAL del contenido de H.E.R. (antes quedaba un espacio negro enorme debajo del primer botón).
// Con "reducir movimiento" el relato sigue corriendo pero solo con fundidos.
// El botón "Ver H.E.R." permite saltarlo. Se salta también con un gesto claro de scroll: rueda, teclas, o un
// deslizamiento HACIA ARRIBA de más de 60 px (un roce o un temblor del dedo ya no lo salta). Un scroll automático
// del navegador en los primeros instantes no lo salta. Si la página se abre ya desplazada, muestra H.E.R. directo.
// Eventos: story_start, story_skip (via: button | wheel | touch | key | scroll | focus | restored) y story_complete.

const R2 = 'https://archivos.simpleymejor.com/';
const PROTOCOLO_COVER = R2 + 'httpsarchivos.simpleymejor.comhercoversher-protocolo-cover.png.png';
export const PHASE_AT = [0, 1800, 3850, 5600];
const OUT_MS = 250;
const GAP_MS = 140;
const IN_MS = 350;
const ENTER_DELAY = OUT_MS + GAP_MS;
const FADE = 'transition-[opacity,transform] duration-700 ease-out';
const CSS = `
@keyframes story-draw{from{stroke-dashoffset:1}to{stroke-dashoffset:0}}
@keyframes story-dot{from{opacity:0}to{opacity:1}}
.story-line{stroke-dasharray:1;stroke-dashoffset:1;animation:story-draw 1.1s cubic-bezier(.22,1,.36,1) .5s forwards}
.story-dot{opacity:0;animation:story-dot .3s ease 1.55s forwards}
@media (prefers-reduced-motion: reduce){.story-line{animation:none;stroke-dashoffset:0}.story-dot{animation:none;opacity:1}}
`;
function SignalLine() {
  return <svg viewBox="0 0 240 32" className="mx-auto mt-6 h-auto w-[72%] max-w-[240px] overflow-visible" fill="none" aria-hidden="true" data-id="element-263" data-mp="11k27tui5oetc">
      <path className="story-line" pathLength={1} d="M6 17 C14 7 23 25 32 16 C41 7 50 24 60 15 C70 7 79 22 89 15 C99 9 108 19 119 16 C130 14 140 17 151 16 L210 16" stroke="#C8A870" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" data-id="element-264" data-mp="skp0p8hi82bs" />
      <circle className="story-dot" cx="216" cy="16" r="2.8" fill="#C8A870" data-id="element-265" data-mp="1lanqx77fq5i1" />
    </svg>;
}
function Phase0() {
  return <>
      <h2 className="font-display font-bold leading-[1.02] tracking-tight text-[#F0E8D8]" style={{
      fontSize: 'clamp(42px, 12vw, 64px)'
    }} data-id="element-266" data-mp="1ggjj6lqchtfw">
        Tu cuerpo<br data-id="element-267" data-mp="2900a8zwh7ty2" />no falló.
      </h2>
      <p className="mt-3 font-display italic leading-[1.08] text-[#C8A870]" style={{
      fontSize: 'clamp(26px, 6vw, 48px)'
    }} data-id="element-268" data-mp="2909rnz3ggss5">
        Cambió la señal.
      </p>
      <SignalLine data-id="element-269" data-mp="2bkeqax9e2lor" />
    </>;
}
function Phase1() {
  return <>
      <p className="font-display text-[#C8A870]/80" style={{
      fontSize: 'clamp(20px, 5vw, 26px)',
      lineHeight: 1.4
    }} data-id="element-270" data-mp="2gig699biq4w0">
        Lo que antes funcionaba,<br data-id="element-271" data-mp="2lfzpyoqzj9vc" />ya no responde igual.
      </p>
      <p className="mt-6 font-display text-[#F0E8D8]/80" style={{
      fontSize: 'clamp(19px, 4.4vw, 26px)',
      lineHeight: 1.45
    }} data-id="element-272" data-mp="2piwc2pfqho41">
        No es cuestión de exigirte más.<br data-id="element-273" data-mp="2ug4umr4snu5t" />Es aprender a acompañar este momento.
      </p>
    </>;
}
function Phase2() {
  return <>
      <p className="font-display font-semibold text-[#F0E8D8]" style={{
      fontSize: 'clamp(22px, 5.2vw, 30px)',
      lineHeight: 1.3
    }} data-id="element-274" data-mp="2zezbvon45ant">
        No necesitás hacer más.
      </p>
      <p className="mt-4 font-display italic text-[#C8A870]/90" style={{
      fontSize: 'clamp(20px, 4.8vw, 28px)',
      lineHeight: 1.4
    }} data-id="element-275" data-mp="3998225oo8rit">
        Necesitás entender qué necesita<br data-id="element-276" data-mp="3i8czt9sesgtx" />tu cuerpo hoy.
      </p>
    </>;
}
function HerIntro({
  onCta,
  revealed,
  calm
}: {
  onCta: () => void;
  revealed: boolean;
  calm: boolean;
}) {
  return <div className="pt-8 text-center" data-id="element-277" data-mp="30k10y9jsuz09">
      <div className="flex items-center gap-4 text-[#C8A870]" data-id="element-278" data-mp="30j96x35up9c3">
        <span className="h-px flex-1 bg-[#C8A870]/30" data-id="element-279" data-mp="3eb5xh2rvfb6d" />
        <span className="font-display text-[30px] font-semibold leading-none tracking-[0.4em] pl-[0.4em]" data-id="element-280" data-mp="3ecqr8q6d2q36">H·E·R</span>
        <span className="h-px flex-1 bg-[#C8A870]/30" data-id="element-281" data-mp="2llyyk2oi8jkl" />
      </div>
      <p className="mt-2 font-display text-[19px] text-[#F0E8D8]/90" data-id="element-282" data-mp="2lmiejdulgxvz">21 días · 3 fases · 1 estrategia</p>

      <h1 className="mt-5 font-display font-semibold leading-[1.04] tracking-tight text-[#F0E8D8]" data-id="element-283" data-mp="2hmq7j5kumzsq">
        <span className="block" style={{
        fontSize: 'clamp(36px, 10.6vw, 52px)'
      }} data-id="element-284" data-mp="2vibtf3vsd3gx">Volvé a sentirte más liviana.</span>
        <span className="block italic text-gradient-gold my-2" style={{
        fontSize: 'clamp(28px, 8vw, 38px)',
        lineHeight: 1.08
      }} data-id="element-285" data-mp="ato0jqdmzokq">Comida, movimiento y descanso</span>
        <span className="block" style={{
        fontSize: 'clamp(26px, 7.4vw, 34px)'
      }} data-id="element-286" data-mp="1nspmrcuekum6">en 21 días.</span>
      </h1>

      <p className="mx-auto mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 font-body text-[12px] font-medium uppercase tracking-[0.16em] text-[#C8A870]" data-id="element-287" data-mp="1ss2hejrin5zb">
        <span data-id="element-288" data-mp="1eudvd8lppsso">✦ Comida real</span>
        <span data-id="element-289" data-mp="1jqm9cio6bfy9">✦ Movimiento suave</span>
        <span data-id="element-290" data-mp="1jrnrf73lx2ph">✦ Hábitos simples</span>
      </p>

      <div className="relative mx-auto mb-8 mt-8 w-full max-w-[19rem]" data-id="element-291" data-mp="27gyp2fpnugze">
        <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center" data-id="element-292" data-mp="27j2oakcm6rtc">
          <div className="h-[75%] w-[75%] rounded-full bg-gold/10 blur-[80px]" data-id="element-293" data-mp="2bk4p35m6jbxf" />
        </div>
        <img src={PROTOCOLO_COVER} alt="Protocolo H.E.R." width={340} height={453} loading="eager" fetchPriority="high" decoding="async" className={`mx-auto block w-full max-w-[340px] rounded-[4px] shadow-[0_25px_60px_rgba(0,0,0,0.45)] ${FADE}`} style={{
        transform: calm || revealed ? 'scale(1)' : 'scale(0.94)',
        transitionDelay: revealed ? `${ENTER_DELAY + 150}ms` : '0s'
      }} data-id="element-294" data-mp="1xp2o8ww6wcrh" />
      </div>

      <div className="mx-auto flex w-full max-w-md flex-col items-center space-y-4" data-id="element-295" data-mp="3036s0x1s3hbr">
        <CTAButton section="hero_story" onClick={onCta} data-id="element-296" data-mp="301c5xfertj63">Acceder a H.E.R. →</CTAButton>
        <p className="text-center font-body text-[11px] uppercase tracking-[0.14em] text-[#C8A870] md:text-[12px]" data-id="element-297" data-mp="2v2921yaj1izp">
          ✦ Acceso inmediato&nbsp;&nbsp; ✦ Garantía 30 días
        </p>
      </div>
    </div>;
}
export function HeroSectionStory() {
  const [reduce] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [restored] = useState(() => typeof window !== 'undefined' && window.scrollY > 8);
  const [phase, setPhase] = useState(restored ? 3 : 0);
  const mountedAtRef = useRef(0);
  const [stageH, setStageH] = useState(() => typeof window !== 'undefined' ? Math.max(560, Math.round(window.innerHeight)) : 780);
  const [finalH, setFinalH] = useState(780);
  const finalRef = useRef<HTMLDivElement>(null);
  const phaseRef = useRef(0);
  const doneRef = useRef(false);
  const timersRef = useRef<number[]>([]);
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const finish = useCallback((via: 'timer' | 'button' | 'wheel' | 'touch' | 'key' | 'scroll' | 'focus') => {
    if (doneRef.current) return;
    doneRef.current = true;
    timersRef.current.forEach(t => window.clearTimeout(t));
    timersRef.current = [];
    setPhase(3);
    trackEvent(via === 'timer' ? 'story_complete' : 'story_skip', {
      variant: 'story',
      via,
      phase: phaseRef.current
    });
  }, []);
  useEffect(() => {
    if (restored) {
      doneRef.current = true;
      trackEvent('story_skip', {
        variant: 'story',
        via: 'restored'
      });
      return;
    }
    mountedAtRef.current = Date.now();
    trackEvent('story_start', {
      variant: 'story',
      reduced: reduce
    });
    const fit = () => setStageH(Math.max(560, Math.round(window.innerHeight)));
    fit();
    window.addEventListener('resize', fit);
    PHASE_AT.slice(1).forEach((ms, i) => {
      timersRef.current.push(window.setTimeout(() => {
        phaseRef.current = i + 1;
        if (i + 1 === 3) finish('timer');else setPhase(i + 1);
      }, ms));
    });
    let touchY: number | null = null;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY > 6) finish('wheel');
    };
    const onTouchStart = (e: TouchEvent) => {
      touchY = e.touches[0]?.clientY ?? null;
    };
    // Solo un deslizamiento claro hacia arriba (intención de bajar) salta el relato.
    const onTouchMove = (e: TouchEvent) => {
      if (touchY !== null && touchY - (e.touches[0]?.clientY ?? touchY) > 60) finish('touch');
    };
    const onKey = (e: KeyboardEvent) => {
      if (['ArrowDown', 'PageDown', 'End', ' '].includes(e.key)) finish('key');
    };
    const onScroll = () => {
      if (window.scrollY > 8 && Date.now() - mountedAtRef.current > 1200) finish('scroll');
    };
    window.addEventListener('wheel', onWheel, {
      passive: true
    });
    window.addEventListener('touchstart', onTouchStart, {
      passive: true
    });
    window.addEventListener('touchmove', onTouchMove, {
      passive: true
    });
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      timersRef.current.forEach(t => window.clearTimeout(t));
      timersRef.current = [];
      window.removeEventListener('resize', fit);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
    };
  }, [restored, reduce, finish]);
  useEffect(() => {
    const el = finalRef.current;
    if (!el) return;
    const measure = () => setFinalH(el.offsetHeight);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  const layers = [<Phase0 key="p0" data-id="element-298" data-mp="3lzf51q8kerah" />, <Phase1 key="p1" data-id="element-299" data-mp="3lzxandh4ahv2" />, <Phase2 key="p2" data-id="element-300" data-mp="3pzfus7146owu" />];
  return <section className="bg-gradient-hero relative overflow-hidden" aria-label="Introducción" data-story-calm={reduce ? 'true' : undefined} data-id="element-301" data-mp="3pynz6w7ior6k">
      <style data-id="element-302" data-mp="hke7wxgtl13f">{CSS}</style>
      <div className="sr-only" data-id="element-303" data-mp="mipixbiuphn8">
        <h2 data-id="element-304" data-mp="miz0captyghb">Tu cuerpo no falló. Cambió la señal.</h2>
        <p data-id="element-305" data-mp="mkjuhz88fxen">Lo que antes funcionaba, ya no responde igual. No es cuestión de exigirte más. Es aprender a acompañar este momento.</p>
        <p data-id="element-306" data-mp="cse2iphzdfzg">No necesitás hacer más. Necesitás entender qué necesita tu cuerpo hoy.</p>
      </div>
      <div className="pointer-events-none absolute bottom-0 left-[8%] top-0 w-px bg-gradient-to-b from-transparent via-gold/15 to-transparent" data-id="element-307" data-mp="10hyntf1lwpbw" />
      <div className="pointer-events-none absolute bottom-0 right-[8%] top-0 w-px bg-gradient-to-b from-transparent via-gold/15 to-transparent" data-id="element-308" data-mp="15arrs0p93tmx" />

      <div className="relative z-10 mx-auto w-full max-w-[480px] px-5" data-id="element-309" data-mp="2l0snql10mxq1">
        <div className="relative" style={{
        // Al terminar, la sección mide lo que mide H.E.R. (sin espacio sobrante debajo del botón).
        height: phase === 3 ? finalH : stageH,
        transition: reduce ? 'none' : 'height 700ms cubic-bezier(.22,1,.36,1)'
      }} data-id="element-310" data-mp="2kyg6yb6zutek">
          {layers.map((content, i) => <div key={i} aria-hidden="true" data-story-phase={i} className="absolute inset-0 flex flex-col items-center justify-center px-1 text-center" style={{
          opacity: phase === i ? 1 : 0,
          transform: reduce ? 'none' : phase === i ? 'translateY(0)' : phase > i ? 'translateY(-10px)' : 'translateY(10px)',
          pointerEvents: 'none',
          transition: phase === i ? `opacity ${IN_MS}ms ease-out ${ENTER_DELAY}ms, transform ${IN_MS}ms ease-out ${ENTER_DELAY}ms` : `opacity ${OUT_MS}ms ease-in, transform ${OUT_MS}ms ease-in`
        }} data-id="element-311" data-mp="272kvgqexckl7" data-mpi={i}>
              {content}
            </div>)}

          <div ref={finalRef} data-story-final onFocusCapture={() => finish('focus')} className="absolute inset-x-0 top-0" style={{
          opacity: phase === 3 ? 1 : 0,
          transform: reduce ? 'none' : phase === 3 ? 'translateY(0)' : 'translateY(12px)',
          pointerEvents: phase === 3 ? 'auto' : 'none',
          transition: phase === 3 ? `opacity 700ms ease-out ${ENTER_DELAY}ms, transform 700ms ease-out ${ENTER_DELAY}ms` : 'opacity 200ms ease-in'
        }} data-id="element-312" data-mp="3bwoag4408cm">
            <HerIntro onCta={scrollToOffer} revealed={phase === 3} calm={reduce} data-id="element-313" data-mp="3gop0vj1o00ui" />
          </div>

          {phase < 3 && <div className="absolute inset-x-0 bottom-2 flex justify-center" data-id="element-314" data-mp="3qgmbankumos6">
              <button type="button" data-story-skip onClick={() => finish('button')} className="min-h-[44px] px-4 text-[12px] uppercase tracking-[0.18em] text-[#C8A870]/80 transition-colors hover:text-[#E8D5B0] focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-315" data-mp="3qipbg0fptyam">
                Ver H.E.R. →
              </button>
            </div>}
        </div>
      </div>
    </section>;
}